/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  forwardRef,
  Input,
  Output,
  ViewChild,
  EventEmitter,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";

// models
import {
  ICNInputControlState,
  ICNAutocompleteFavoriteType,
  ICNAutocompleteFavoriteItemFormat,
} from "./models/bfx-interface-controls.enums";
import { BfxAutocompleteFavoritesProviderService } from "./services/bfx-autocomplete-favorites-provider.service";

export const IC_CCY_PAIR_STRING_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICCcyPairStringComponent),
  multi: true,
};
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { OERateDisplayModel } from "../../data/models/rate/oe-rate-display.model";
import { ICIAutocomplete } from "./interface/ic-autocomplete.interface";
import { ICAutocompleteGenericModel } from "./models/bfx-interface-controls.models";

@Component({
  selector: "ic-ccy-pair-string",
  templateUrl: "./html/ic-ccy-pair-string.html",
  providers: [IC_CCY_PAIR_STRING_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICCcyPairStringComponent
  implements OnInit, OnDestroy, ControlValueAccessor, ICIAutocomplete
{
  /***************************************/
  /* COMPONENT INTERFACE DATA */

  @Input() entryType: OENEntryType = OENEntryType.SPOT;

  @Input() allowEmpty: boolean = false;

  @Input() label: string = "Pair";

  private _dataset: Array<any>;
  public get dataset(): Array<any> {
    return this._dataset;
  }
  @Input()
  public set dataset(value: Array<any>) {
    if (this._dataset !== value) {
      this._dataset = value;
      this.setDataset(value);
    }
  }

  @Input() marketRateDisable: boolean = false;

  private _disabled: boolean;

  public get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  public set disabled(value: boolean) {
    this._disabled = value;

    if (typeof value === "boolean") {
      this.data.disabled = value;
    } else {
      this.data.disabled = false;
    }
    // this.cd.markForCheck();
  }

  private _inverted: boolean;
  public get inverted(): boolean {
    return this._inverted;
  }

  @Input()
  public set inverted(value: boolean) {
    this._inverted = value;
    if (typeof value === "boolean") {
      this.data.inverted = value;
    } else {
      this.data.inverted = false;
    }
    this.cd.markForCheck();
  }

  @Output() blur: EventEmitter<string> = new EventEmitter<string>();

  @Output() focus: EventEmitter<void> = new EventEmitter<void>();

  /* External Validation */
  input_externalState: ICNInputControlState;

  get externalState() {
    return this.input_externalState;
  }

  @Input()
  set externalState(value: any) {
    this.input_externalState = value;
    if (this.input_externalState != null) {
      this.data.state = this.input_externalState;
    }
  }

  /***************************************/
  /* DATA */
  public config: any = {
    resultsToShow: 30,
    favoritesSize: 3, // default value, loaded on ngOnInit
    removeFavoriteDuplicates: true,
  };
  public data: {
    item: string;
    value: string;
    dataset: Array<string>;
    filteredItems: Array<string>;
    inverted: boolean;
    disabled: boolean;
    state: ICNInputControlState;
  } = {
    item: null,
    value: null, // string
    dataset: [],
    filteredItems: [],
    inverted: false,
    disabled: false,
    state: ICNInputControlState.INACTIVE,
  };

  public marketRate: OERateDisplayModel = new OERateDisplayModel();

  public locked: boolean = false;

  public inputFocused: boolean = false;

  @ViewChild("inputField", { static: true }) inputField: ElementRef;

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    public element: ElementRef,
    public translationService: TranslateService,
    protected favoritesService: BfxAutocompleteFavoritesProviderService,
    public cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {}

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    let favType: ICNAutocompleteFavoriteType = this.getFavType(this.entryType);
    this.config.favoritesSize = this.favoritesService.getFavoritesListSize(favType);

    if (this.dataset.length !== this.data.dataset.length) {
      this.setDataset(this.dataset);
    }
    if (this.inverted != null) {
      this.data.inverted = this.inverted;
    } else {
      this.data.inverted = false;
    }

    if (this.input_externalState != null) {
      this.data.state = this.input_externalState;
    }
  }

  protected getFavType(entryType: OENEntryType): ICNAutocompleteFavoriteType {
    let favType: ICNAutocompleteFavoriteType = ICNAutocompleteFavoriteType.CCY_PAIR;
    switch (this.entryType) {
      case OENEntryType.NDF: {
        favType = ICNAutocompleteFavoriteType.CCY_PAIR_NDF;
        break;
      }
      case OENEntryType.BENCHMARK: {
        favType = ICNAutocompleteFavoriteType.CCY_PAIR_BMARK;
        break;
      }
    }
    return favType;
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(ccyPair: string): void {
    this.data.value = ccyPair;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* Event Handlers */
  public onFocus(_): void {
    this.inputFocused = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.focus.emit();
  }

  public onBlur(_): void {
    this.cd.markForCheck();
    this.onModelTouched();
    this.inputFocused = false;
    this.set(this.data.item); // for formatting
    this.updateValue(this.data.item);
    this.blur.emit(this.data.value);
  }

  public onSelect($event): void {
    this.onBlur($event);
  }

  /**********************************************************************/
  /* PUBLIC METHODS */

  public updateValue(ccyPair: string): void {
    //	logger.out.info("DEV-CCY-PAIR UPDATE-VALUE: ccyPair=", ccyPair);
    if (this.validate()) {
      let ccyPairValue = typeof ccyPair === "string" ? ccyPair.toUpperCase() : null;

      this.onModelChange(ccyPairValue);
      this.writeValue(ccyPairValue);

      let favType: ICNAutocompleteFavoriteType = this.getFavType(this.entryType);
      this.favoritesService.addFavoriteItem(favType, ccyPairValue);
    } else {
      this.onModelChange(null);
      this.writeValue(null);
    }
    // this.cd.markForCheck();
  }

  public set(ccyPair: string): void {
    if (ccyPair === null || ccyPair === "") {
      this.data.item = null;
    } else if (typeof ccyPair === "string") {
      let ccyPairModel = this.data.dataset.find(
        (item) => item.toLowerCase() === ccyPair.toLowerCase(),
      );
      this.data.item = ccyPairModel != null ? ccyPairModel : ccyPair.toUpperCase();
    } else {
      logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid value`);
    }
    // this.cd.markForCheck();
  }

  public setDataset(dataset: Array<any>): void {
    if (dataset instanceof Array && dataset.length > 0) {
      this.data.dataset = dataset;
    } else {
      this.data.dataset = [];
    }
    // this.cd.markForCheck();
  }

  public reset() {
    this.label = "Pair";
    //

    this.data.disabled = false;

    this.data.value = null;
    this.data.item = null;
    this.data.inverted = false;

    this.marketRate = new OERateDisplayModel();
    this.marketRate.reset();
    this.data.dataset = [];
    this.data.filteredItems = [];

    this.externalState = null;

    this.initialize();
    this.data.state = ICNInputControlState.INACTIVE;
    this.updateValue(null);
    this.cd.markForCheck();
  }

  public lock(): void {
    this.locked = true;
    this.cd.markForCheck();
  }

  public unlock(): void {
    this.locked = false;
    this.cd.markForCheck();
  }

  public load(ccyPair: string, propagate?: boolean): void {
    this.set(ccyPair); //this.transformItemToGenericModel(ccyPair);
    this.validate();
    if (typeof propagate === "boolean" && propagate) {
      this.updateValue(ccyPair);
    }
    this.cd.markForCheck();
  }

  public validate(): boolean {
    try {
      // reset internal state
      this.externalState = null;

      let itemValue = this.data.item;

      if (typeof itemValue === "string" && itemValue.length === 6) {
        let selectedModel = this.data.dataset.find(
          (item) => item.toLowerCase() === itemValue.toLowerCase(),
        );
        if (selectedModel != null) {
          this.data.state = ICNInputControlState.VALID;
          return true;
        } else {
          this.data.state = ICNInputControlState.INVALID;
          this.data.inverted = false;
          return false;
        }
      } else if (itemValue == null || itemValue === "") {
        if (this.data.state !== ICNInputControlState.INACTIVE && !this.allowEmpty) {
          this.data.state = ICNInputControlState.INVALID;
          this.data.inverted = false;
          return false;
        } else if (this.data.state !== ICNInputControlState.INACTIVE && this.allowEmpty) {
          this.data.state = ICNInputControlState.INACTIVE;
          this.data.inverted = false;
          return true;
        } else {
          // else means => this.data.state === ICNInputControlState.INACTIVE
          return true;
        }
      } else {
        this.data.state = ICNInputControlState.INVALID;
        this.data.inverted = false;
        return false;
      }
    } catch (ex) {
      logger.out.error("ERROR: ICCcyPairComponent.validatePair(): Exception:", ex);
      this.data.state = ICNInputControlState.INVALID;
      this.data.inverted = false;
      return false;
    } finally {
      this.cd.markForCheck();
    }
  }

  /**********************************************************************/
  /* RATE */

  public setMarketRate(value: OERateDisplayModel): void {
    if (value instanceof OERateDisplayModel) {
      this.marketRate = Object.assign(new OERateDisplayModel(), value);
    }
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* AUTOCOMPLETE */

  public search($event: any): void {
    let filteredItems = [];
    let itemsAll = this.data.dataset;
    let searchToken = $event.query;

    // HANDLE FAVORITES
    let favType: ICNAutocompleteFavoriteType = this.getFavType(this.entryType);

    const favoriteCcyPairs: Array<ICAutocompleteGenericModel> =
      this.favoritesService.getFavoriteItems(favType, ICNAutocompleteFavoriteItemFormat.DISPLAY);
    let enabledFavoriteCcyPairs: Array<ICAutocompleteGenericModel> = favoriteCcyPairs.filter(
      (ccyPair) => this.data.dataset.includes(ccyPair.value),
    );
    if (enabledFavoriteCcyPairs != null && enabledFavoriteCcyPairs.length > 0) {
      if (searchToken == null || searchToken === "") {
        filteredItems = enabledFavoriteCcyPairs;
      } else {
        filteredItems = enabledFavoriteCcyPairs.filter((item) => {
          return item.value.toLowerCase().startsWith(searchToken.toLowerCase());
        });
      }
      // sort favorites
      if (filteredItems != null && filteredItems.length > 1) {
        filteredItems.sort((a, b) => a.value.localeCompare(b.value));
      }
      // remove if too many
      while (filteredItems.length > this.config.resultsToShow) {
        filteredItems.pop();
      }
      // indicate where the line should be drawn, "Here and no further!"
      if (filteredItems.length > 0) {
        filteredItems[filteredItems.length - 1].lastFavorite = true;
      }
    }

    // HANDLE REGULAR SUGGESTIONS
    let favoritesSize = filteredItems.length;
    let suggestions = new Array<string>();
    for (let i = 0; i < itemsAll.length; i++) {
      let item = itemsAll[i];
      if (searchToken.length === 0 || item.toLowerCase().startsWith(searchToken.toLowerCase())) {
        // avoid duplicates
        if (
          !this.config.removeFavoriteDuplicates ||
          (filteredItems.findIndex((favItem) => {
            return favItem.value === item;
          }) < 0 &&
            suggestions.findIndex((inItem) => {
              return inItem === item;
            }) < 0)
        ) {
          suggestions.push(item);
        }
      }

      if (
        this.config.resultsToShow > 0 &&
        suggestions.length >= this.config.resultsToShow - favoritesSize
      ) {
        suggestions.sort((a, b) => a.localeCompare(b));
        this.data.filteredItems = filteredItems
          .map((itemElement) => itemElement.value)
          .concat(suggestions);
        return;
      }
    }
    suggestions.sort((a, b) => a.localeCompare(b));
    this.data.filteredItems = filteredItems
      .map((itemElement) => itemElement.value)
      .concat(suggestions);
    // this.cd.markForCheck();
  }

  /**********************************************************************/
  /* TRANSFORMERS */
  /* 	private transformArray(data: Array<string>): Array<ICAutocompleteGenericModel> {
		let result: Array<ICAutocompleteGenericModel> = Array<ICAutocompleteGenericModel>();
		if(data != null && data.length > 0){
			data.forEach((item)=>{
				try{
					result.push(this.transform(item));
				}catch(ex){
					logger.out.warn(ex);
				}
			});
		}
		return result;
	}

	protected transform(item: string): ICAutocompleteGenericModel{
		if(item != null){
			return new ICAutocompleteGenericModel(item, item);
		}else{
			throw('Can not transform as CcyPair is null!');
		}
	} */
}
